import { useState } from "react";
import { Plus, Trash2, Upload, Loader2 } from "lucide-react";
import { api, img, formatError } from "../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-volt focus:ring-1 focus:ring-volt/40";

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-white/50">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-xs text-white/30">{hint}</span>}
  </label>
);

export const Text = ({ label, value, onChange, placeholder, hint, testid }) => (
  <Field label={label} hint={hint}>
    <input data-testid={testid} className={inputCls} value={value ?? ""} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} />
  </Field>
);

export const Area = ({ label, value, onChange, rows = 4, hint, testid }) => (
  <Field label={label} hint={hint}>
    <textarea data-testid={testid} rows={rows} className={inputCls} value={value ?? ""}
      onChange={(e) => onChange(e.target.value)} />
  </Field>
);

export const Btn = ({ children, onClick, variant = "primary", type = "button", disabled, testid }) => {
  const base = "inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0";
  const styles = {
    primary: "brand-gradient text-white",
    ghost: "border border-white/15 text-white hover:bg-white/5",
    danger: "border border-red-500/40 text-red-300 hover:bg-red-500/10",
  };
  return (
    <button data-testid={testid} type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  );
};

export const ImageUpload = ({ label, value, onChange, hint, testid }) => {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
          {value ? <img src={img(value)} alt="" className="h-full w-full object-cover" /> : <Upload size={18} className="text-white/30" />}
        </div>
        <div className="flex-1">
          <input className={`${inputCls} mb-2`} value={value ?? ""} placeholder="Image URL or upload →" onChange={(e) => onChange(e.target.value)} />
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 text-xs font-medium text-white hover:bg-white/5">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading..." : "Upload"}
            <input data-testid={testid} type="file" accept="image/*" className="hidden" onChange={handle} disabled={busy} />
          </label>
        </div>
      </div>
    </Field>
  );
};

export const StringList = ({ label, items = [], onChange, placeholder }) => {
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const add = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <Field label={label}>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input className={inputCls} value={it} placeholder={placeholder} onChange={(e) => update(i, e.target.value)} />
            <button type="button" onClick={() => remove(i)} className="rounded-lg border border-white/10 px-3 text-white/50 hover:text-red-300">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 text-xs font-medium text-volt hover:opacity-80">
          <Plus size={14} /> Add item
        </button>
      </div>
    </Field>
  );
};

export const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl border border-white/10 bg-korio-surface p-6 ${className}`}>{children}</div>
);
